import React, { useEffect, useRef } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { ChartTitle } from '../components/styles';
import Spacer from '../../shared/Spacer';
import {
  setupBaseChart,
  setupCategoryAxisBase,
  setupLegendBase,
  setupMarkerColumnBase,
  setupSeriesTooltipBase,
  setupValueAxisBase,
} from 'features/shared/ui/charts';
import { useClickStreamContext } from 'features/shared/ClickStream';

interface Props {
  title: string;
  yAxisTitle: string;
  colors: am4core.Color[];
  data: ChartData[];
}

export interface ChartData {
  firstTest?: number;
  firstTotal?: number;
  secondTest?: number;
  secondTotal?: number;
  thirdTest?: number;
  thirdTotal?: number;
  parallel: string;
}

interface ChartDataWithTotalDeltas extends ChartData {
  firstToTotal?: number;
  secondToTotal?: number;
  thirdToTotal?: number;
}

const setupLegend = () => {
  const legend = setupLegendBase();

  legend.position = 'top';
  legend.dy = -30;

  const marker = legend.markers.template;

  setupMarkerColumnBase(marker, true);

  return legend;
};

function setupCategoryAxis(categoryAxis: am4charts.CategoryAxis) {
  setupCategoryAxisBase(categoryAxis);

  categoryAxis.dataFields.category = 'parallel';
  categoryAxis.renderer.cellStartLocation = 0.15;
  categoryAxis.renderer.cellEndLocation = 0.85;
  categoryAxis.layout = 'absolute';
  categoryAxis.paddingRight = 0;
  categoryAxis.paddingTop = 0;
  categoryAxis.title.text = 'Классы';
  categoryAxis.title.fill = am4core.color('#7D89A1');
  categoryAxis.title.align = 'right';
  categoryAxis.title.dy = 10;
  categoryAxis.title.dx = 40;
}

function setupValueAxis(valueAxis: am4charts.ValueAxis, yAxisTitle: string) {
  valueAxis.minY = 0;
  valueAxis.min = 0;
  valueAxis.paddingTop = 0;
  valueAxis.paddingLeft = 0;
  valueAxis.layout = 'absolute';
  valueAxis.title.text = yAxisTitle;
  valueAxis.title.valign = 'top';
  valueAxis.title.dy = -90;
  valueAxis.title.dx = 20;
  valueAxis.title.fontSize = 12;

  setupValueAxisBase(valueAxis);
}

function setupSeries(
  series: am4charts.ColumnSeries,
  field: string,
  name: string,
  stacked: boolean,
  totalField: string,
) {
  setupSeriesTooltipBase(series);

  series.columns.template.width = am4core.percent(70);
  series.dataFields.valueY = field;
  series.dataFields.categoryX = 'parallel';
  series.name = name;

  if (stacked) {
    series.columns.template.tooltipText = `{${totalField}}`;

    series.hiddenInLegend = true;
  } else {
    series.columns.template.tooltipText = '{valueY}';
  }

  series.stacked = stacked;
}

function setupChart(element: HTMLDivElement, yAxisTitle: string, colors: am4core.Color[]) {
  let chart = am4core.create(element, am4charts.XYChart);

  setupBaseChart(chart);

  chart.paddingTop = 26;
  chart.paddingRight = 40;
  chart.paddingLeft = 0;
  chart.paddingBottom = 0;
  chart.maskBullets = false;

  let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
  setupCategoryAxis(categoryAxis);

  let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
  setupValueAxis(valueAxis, yAxisTitle);

  chart.colors.list = colors;

  function createSeries(field: string, name: string, stacked = false, totalField = '') {
    const series = chart.series.push(new am4charts.ColumnSeries());
    setupSeries(series, field, name, stacked, totalField);
  }

  createSeries('firstTest', '— Первый тест (22 сен – 23 ноя 2025)');
  createSeries('firstToTotal', '— Первый тест (регион)', true, 'firstTotal');
  createSeries('secondTest', '— Второй тест (19 янв – 1 мар 2026)');
  createSeries('secondToTotal', '— Второй тест (регион)', true, 'secondTotal');
  createSeries('thirdTest', '— Третий тест (6 апр – 31 мая 2026)');
  createSeries('thirdToTotal', '— Третий тест (регион)', true, 'thirdTotal');

  chart.legend = setupLegend();
  return chart;
}

// am4chart не умеет одновременно отображать наложение столбиков в графике и группировать столбики по 3 одновременно,
// для этого вводим вспомогательные значения, которые равны разнице total и текущего значения на графике
function countTotals(mock: ChartData[]): ChartDataWithTotalDeltas[] {
  return mock.map((item) => {
    const newItem: ChartDataWithTotalDeltas = { ...item };
    if (item.firstTotal) {
      newItem.firstToTotal = item.firstTotal - item.firstTest!;
    }
    if (item.secondTotal) {
      newItem.secondToTotal = item.secondTotal - item.secondTest!;
    }
    if (item.thirdTotal) {
      newItem.thirdToTotal = item.thirdTotal - item.thirdTest!;
    }
    return newItem;
  });
}

const Component = ({ title, yAxisTitle, colors, data }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const chart = useRef<am4charts.XYChart>();
  const { logPageEvent } = useClickStreamContext();

  useEffect(() => {
    if (chart.current) {
      chart.current?.dispose();
      chart.current = undefined;
    }

    chart.current = setupChart(ref.current!, yAxisTitle, colors);
    chart.current.data = countTotals(data);
    const handleClick = () => {
      logPageEvent({
        eventType: 'monitoring_test_results_click_on_chart_legend',
        eventInfo: 'Срезы знаний|Результаты тестирования: Клик по легенде графика ' + title,
      });
    };
    chart.current.legend.dom.addEventListener('click', handleClick);

    return () => {
      chart.current?.legend?.dom?.removeEventListener('click', handleClick);
      chart.current!.dispose();
    };
  }, [data]);

  return (
    <div>
      <ChartTitle>{title}</ChartTitle>
      <Spacer size={34} />
      <div ref={ref} style={{ width: '450px', height: '410px' }} />
    </div>
  );
};

export default Component;
